// BT-9799 — Flattened superadmin navigation.
//
// Single source of truth for the flattened menu. It drives three things:
//   1. Sidenav.js      — the flat top-level sidebar items (+ the one nested
//                        exception, Pipelines).
//   2. SubTabs.jsx     — the folder-style subtab strip shown for a module that
//                        has subtabs.
//   3. Header.js       — the module title shown in the top navbar.
//
// Each module owns a `match(pathname)` predicate so we can resolve the active
// module/subtab from any route (including deep drill-down routes like
// /home/accounts/:module or /home/permissions/:role). Highlighting is derived
// from these predicates rather than react-router's default exact matching so a
// top-level item stays lit while you move between its subtabs.

const startsWith = (prefix) => (pathname) => pathname.startsWith(prefix);
const oneOf = (...prefixes) => (pathname) => prefixes.some((p) => pathname.startsWith(p));

export const NAV_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'icon-home2',
    path: '/home/dashboard',
    match: startsWith('/home/dashboard'),
  },
  {
    key: 'user-activity',
    label: 'User Activity',
    // The module itself is the "User Activity Explorer"; the menu item is just
    // "User Activity" (BT-9799 rename).
    title: 'User Activity Explorer',
    icon: 'icon-pulse2',
    path: '/home/activity-log',
    match: startsWith('/home/activity-log'),
  },
  {
    // BT-10335: the Dashboard Phase 0 baseline (BT-10324 telemetry), read per
    // visit rather than per row like the Explorer above. 'Dashboard' is the
    // Ops Dashboard's key; this one is named for what it measures.
    key: 'dashboard-usage',
    label: 'Dashboard Usage',
    icon: 'icon-stats-dots',
    path: '/dashboard-insights/overview',
    match: startsWith('/dashboard-insights'),
    subtabs: [
      { label: 'Overview', path: '/dashboard-insights/overview', match: startsWith('/dashboard-insights/overview') },
      { label: 'Customers', path: '/dashboard-insights/customers', match: startsWith('/dashboard-insights/customers') },
    ],
  },
  {
    key: 'accounts',
    label: 'Accounts',
    icon: 'icon-office',
    path: '/home/account',
    // /home/accounts/:module are the per-account drill-down pages — still the
    // Accounts module.
    match: oneOf('/home/account', '/home/TierConformance', '/home/AccountRecordFreshness'),
    subtabs: [
      { label: 'Accounts', path: '/home/account', match: oneOf('/home/account', '/home/accounts') },
      { label: 'Tier Conformance', path: '/home/TierConformance', match: startsWith('/home/TierConformance') },
      { label: 'Records Recency', path: '/home/AccountRecordFreshness', match: startsWith('/home/AccountRecordFreshness') },
    ],
  },
  {
    key: 'users',
    label: 'Users',
    icon: 'icon-user',
    path: '/home/AccountReports',
    match: oneOf('/home/AccountReports', '/home/lockouts', '/home/broker-domains'),
    subtabs: [
      { label: 'Users', path: '/home/AccountReports', match: startsWith('/home/AccountReports') },
      {
        label: 'Locked Users',
        path: '/home/lockouts',
        match: (p) => p.startsWith('/home/lockouts') && !p.startsWith('/home/lockouts/blocked-ips'),
      },
      { label: 'Blocked IPs', path: '/home/lockouts/blocked-ips', match: startsWith('/home/lockouts/blocked-ips') },
      // BT-10968
      { label: 'Broker Domains', path: '/home/broker-domains', match: startsWith('/home/broker-domains') },
    ],
  },
  {
    key: 'aircraft',
    label: 'Aircraft',
    icon: 'icon-airplane2',
    path: '/home/aircraft',
    match: oneOf('/home/aircraft', '/home/AircraftRecordFreshness', '/home/VeryonUnauthorized'),
    subtabs: [
      { label: 'Aircraft', path: '/home/aircraft', match: startsWith('/home/aircraft') },
      { label: 'Records Recency', path: '/home/AircraftRecordFreshness', match: startsWith('/home/AircraftRecordFreshness') },
      // BT-10030: aircraft whose Veryon sync is stalled on HTTP 403.
      { label: 'Veryon Access', path: '/home/VeryonUnauthorized', match: startsWith('/home/VeryonUnauthorized') },
    ],
  },
  {
    key: 'files',
    label: 'Files',
    icon: 'icon-books',
    path: '/home/files',
    match: startsWith('/home/files'),
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: 'icon-file-text2',
    path: '/home/documents',
    match: startsWith('/home/documents'),
  },
  {
    key: 'email',
    label: 'Email',
    icon: 'icon-envelop',
    path: '/home/Unidentified',
    match: startsWith('/home/Unidentified'),
    subtabs: [
      { label: 'Unidentified Email Attachments', path: '/home/Unidentified', match: startsWith('/home/Unidentified') },
    ],
  },
  {
    key: 'pipelines',
    label: 'Pipelines',
    icon: 'icon-tree7',
    // The one exception to the flatten: Pipelines keeps a nested sidebar
    // submenu (its stage modules already carry their own sub-tabs), so it
    // renders `children` in the sidebar and shows NO subtab strip.
    nested: true,
    path: '/home/pipeline',
    // BT-10174: '/home/sendtobadass' was the old Send-to-BADASS screen, removed with it.
    match: oneOf('/home/pipeline', '/home/badass-versions', '/home/reprocess'),
    children: [
      // Each child carries its own `match`, same as every module and subtab above, so
      // Sidenav.js can resolve which one is active the same way it resolves the top-level
      // module — no per-item special-casing there.
      //
      // Overview's path ('/home/pipeline') is a literal string-prefix of every other
      // child's path ('/home/pipelines/...'), so plain startsWith would also light it up
      // on every other stage's route. Anchor it to its own segment instead.
      {
        label: 'Overview',
        path: '/home/pipeline',
        icon: 'icon-stats-bars',
        match: (p) => p === '/home/pipeline' || p.startsWith('/home/pipeline/'),
      },
      { label: 'Metadata', path: '/home/pipelines/metadata', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/metadata') },
      { label: 'Homogenization', path: '/home/pipelines/homogenization', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/homogenization') },
      { label: 'Thumbnailing', path: '/home/pipelines/thumbnailing', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/thumbnailing') },
      { label: 'Textract', path: '/home/pipelines/textract', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/textract') },
      { label: 'OpenSearch', path: '/home/pipelines/opensearch', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/opensearch') },
      { label: 'BADASS', path: '/home/pipelines/badass', icon: 'icon-stats-bars', match: startsWith('/home/pipelines/badass') },
      // BT-10174: the superadmin re-processing tool. Sits under Pipelines because that is
      // where the extraction pipeline it drives already lives.
      { label: 'Re-process files', path: '/home/reprocess', icon: 'icon-share2', match: startsWith('/home/reprocess') },
    ],
  },
  {
    key: 'barrel-roll',
    label: 'Barrel Roll',
    icon: 'icon-lab',
    path: '/ai-tools/barrel-roll-sessions',
    match: startsWith('/ai-tools'),
    subtabs: [
      { label: 'Sessions', path: '/ai-tools/barrel-roll-sessions', match: startsWith('/ai-tools/barrel-roll-sessions') },
      { label: 'Memory', path: '/ai-tools/barrel-roll-memories', match: startsWith('/ai-tools/barrel-roll-memories') },
      // Eval + Test Fixtures are Product-only (BT-9799) — hidden from non-Product
      // superadmins via useIsProduct() in SubTabs.
      { label: 'Evals', path: '/ai-tools/barrel-roll-eval', match: startsWith('/ai-tools/barrel-roll-eval'), productOnly: true },
      { label: 'Test Fixtures', path: '/ai-tools/barrel-roll-fixtures', match: startsWith('/ai-tools/barrel-roll-fixtures'), productOnly: true },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: 'icon-clipboard',
    path: '/compliance-insights/overview',
    match: startsWith('/compliance-insights'),
    subtabs: [
      { label: 'Overview', path: '/compliance-insights/overview', match: startsWith('/compliance-insights/overview') },
      { label: 'Customers', path: '/compliance-insights/customers', match: startsWith('/compliance-insights/customers') },
      { label: 'Projects', path: '/compliance-insights/projects', match: startsWith('/compliance-insights/projects') },
      // BT-9848: Auto-Research Accuracy — behavioral accuracy of Auto Research
      // judged by human accept/reject decisions. Empty in prod until Auto
      // Research is activated there (BT-9484); QA has data today.
      { label: 'Auto-Research Accuracy', path: '/compliance-insights/accuracy', match: startsWith('/compliance-insights/accuracy') },
      // BT-10403: Auto Research AI spend (per-item Barrel Roll session cost).
      { label: 'Cost', path: '/compliance-insights/cost', match: startsWith('/compliance-insights/cost') },
      // BT-10404: Auto Research speed next to its cost.
      { label: 'Executive', path: '/compliance-insights/executive', match: startsWith('/compliance-insights/executive') },
    ],
  },
  {
    key: 'platform-config',
    label: 'Platform Config',
    icon: 'icon-cog',
    path: '/home/permissions/owner',
    match: oneOf(
      '/home/permissions',
      '/home/notifications',
      '/home/Users/Users',
      '/home/AddSuperAdmin',
      '/home/feature-flags',
      '/home/oauth-clients',
    ),
    subtabs: [
      { label: 'Role Configuration', path: '/home/permissions/owner', match: startsWith('/home/permissions') },
      { label: 'Notifications', path: '/home/notifications/superadmin', match: startsWith('/home/notifications') },
      { label: 'SuperAdmin Users', path: '/home/Users/Users', match: oneOf('/home/Users/Users', '/home/AddSuperAdmin') },
      { label: 'Feature Flags', path: '/home/feature-flags', match: startsWith('/home/feature-flags') },
      { label: 'OAuth Clients', path: '/home/oauth-clients', match: startsWith('/home/oauth-clients') },
    ],
  },
];

// Routes that aren't a top-level nav module but still need a header title.
const ORPHAN_TITLES = [
  { match: startsWith('/home/Profile'), title: 'My Profile' },
];

export function resolveActiveModule(pathname) {
  return NAV_MODULES.find((m) => m.match(pathname)) || null;
}

export function resolveActiveSubtab(module, pathname) {
  if (!module || !module.subtabs) return null;
  return module.subtabs.find((t) => t.match(pathname)) || module.subtabs[0];
}

// BT-10174: same shape as resolveActiveSubtab, for the one nested module (Pipelines).
// Unlike subtabs there's no "module == first child" default — a pipeline child is only
// active when its own match hits, so a Pipelines route with no matching child (there
// shouldn't be one) simply lights up no child, not the wrong one.
export function resolveActiveChild(module, pathname) {
  if (!module || !module.children) return null;
  return module.children.find((c) => c.match && c.match(pathname)) || null;
}

export function resolveModuleTitle(pathname) {
  const module = resolveActiveModule(pathname);
  if (module) return module.title || module.label;
  const orphan = ORPHAN_TITLES.find((o) => o.match(pathname));
  return orphan ? orphan.title : '';
}
